import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BrainCircuit, Clock3, Medal, RefreshCcw, ShieldCheck, Sparkles, Star, Target, TrendingUp, Trophy, Zap } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { EmptyState, ErrorState, Loading, MasteryBar, formatDuration } from "@/components/States";
import { Button } from "@/components/ui/button";

export default function StudentProgress() {
  const [data, setData] = useState(null);
  const [revenge, setRevenge] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const progressRes = await api.get("/student/progress");
      setData(progressRes.data);
      try {
        const revengeRes = await api.get("/student/revenge");
        setRevenge(revengeRes.data.items || []);
      } catch {
        setRevenge([]);
      }
    } catch (err) {
      setError(apiError(err, "No pudimos cargar tu progreso."));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading />;

  const { totals, mastery, weaknesses, strengths, recent_sessions: recent, exam_cycles: cycles = [], xp_balance: xpBalance = 0 } = data;
  const latestCycle = cycles[0];

  return (
    <div className="student-page" data-testid="student-progress">
      <div className="play-card relative overflow-hidden bg-gradient-to-r from-cyan-400 to-blue-500 p-6 text-slate-950 sm:p-8">
        <span className="play-doodle -right-8 -top-12 h-40 w-40 border-[25px] border-white/20" />
        <span className="play-icon h-16 w-16 bg-slate-900"><TrendingUp className="h-9 w-9 animate-wiggle" /></span>
        <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight">Tu aventura está creciendo</h1>
        <p className="mt-1 max-w-xl font-semibold text-cyan-950/70">Cada respuesta suma experiencia. Mira todo lo que ya conquistaste.</p>
      </div>

      <section className="play-card overflow-hidden bg-slate-900 p-5 text-white sm:p-7" data-testid="xp-overview">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="play-icon h-16 w-16 bg-fuchsia-500 text-white"><Zap className="h-9 w-9 fill-current animate-pulse" /></span>
            <div>
              <p className="text-sm font-extrabold uppercase tracking-[.18em] text-fuchsia-300">Experiencia total</p>
              <p className="font-display text-4xl font-black">{xpBalance.toLocaleString()} XP</p>
            </div>
          </div>
          <p className="max-w-md text-sm font-semibold text-slate-300">Tu XP premia el esfuerzo, la mejora y aprender de tus errores. La nota es solo una parte de tu aventura.</p>
        </div>

        {latestCycle && (
          <div className="mt-6">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div><p className="font-display text-xl font-extrabold">{latestCycle.subject} · {latestCycle.title}</p><p className="text-sm text-slate-400">Último ciclo de examen</p></div>
              <p className="font-display text-3xl font-black text-lime-300">+{latestCycle.totalXP} XP</p>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
              <XpPill label="Esfuerzo" value={latestCycle.effortXP} tone="bg-cyan-400 text-cyan-950" />
              <XpPill label="Mejora" value={latestCycle.improvementXP} tone="bg-lime-300 text-lime-950" />
              <XpPill label="Nota" value={latestCycle.gradeXP} tone="bg-amber-300 text-amber-950" />
              <XpPill label="Revenge" value={latestCycle.correctionXP} tone="bg-pink-400 text-pink-950" />
              <XpPill label="Bonus" value={latestCycle.bonusXP} tone="bg-violet-400 text-violet-950" />
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <Metric label="Exam Ready" value={`${latestCycle.examReady || 0}%`} icon={<Target className="h-5 w-5" />} />
              <Metric label="Growth" value={latestCycle.growth == null ? "Baseline" : `${latestCycle.growth > 0 ? "+" : ""}${latestCycle.growth}%`} icon={<TrendingUp className="h-5 w-5" />} />
              <Metric label="Errores dominados" value={`${latestCycle.errorsCorrected}/${latestCycle.errorsTotal}`} icon={<ShieldCheck className="h-5 w-5" />} />
            </div>
          </div>
        )}
      </section>

      {revenge.length > 0 && (
        <section className="play-card bg-gradient-to-br from-fuchsia-100 via-orange-100 to-amber-100 p-5 sm:p-6" data-testid="revenge-mode">
          <div className="flex items-center gap-3"><span className="play-icon h-14 w-14 bg-fuchsia-600"><Trophy className="h-8 w-8" /></span><div><p className="text-xs font-black uppercase tracking-[.2em] text-fuchsia-700">Revenge Mode</p><h2 className="font-display text-2xl font-black text-slate-900">Convierte cada error en un superpoder</h2></div></div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {revenge.map((mission) => (
              <article key={mission.id} className="rounded-3xl border-2 border-white bg-white/80 p-4 shadow-sm">
                <p className="text-xs font-bold uppercase text-fuchsia-600">{mission.exam}</p>
                <h3 className="mt-1 font-display text-lg font-extrabold text-slate-900">{mission.topic}</h3>
                <p className="mt-2 text-sm font-semibold text-slate-500">{mission.status === "mastered" ? "¡Dominado! La revancha es tuya." : "Revisa, practica una variante y demuestra que lo recuerdas."}</p>
                <div className="mt-3 flex items-center gap-2 text-xs font-black text-slate-600"><Sparkles className="h-4 w-4 text-amber-500" /> Hasta 35 XP por concepto</div>
              </article>
            ))}
          </div>
        </section>
      )}

      {totals.questions_answered === 0 ? (
        <EmptyState
          title="Todavia sin datos"
          description="Cuando practiques veras aqui tu dominio por tema."
          action={<Link to="/s"><Button className="play-button estudia-touch rounded-2xl bg-violet-600 font-extrabold hover:bg-violet-500">Elegir una misión</Button></Link>}
          testId="progress-empty"
        />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="play-card flex items-center gap-4 bg-amber-300 p-5" data-testid="progress-time">
              <span className="play-icon h-16 w-16 bg-orange-500"><Clock3 className="h-9 w-9" /></span>
              <div><p className="text-sm font-extrabold text-amber-950/70">Tiempo en misión</p>
              <p className="font-display text-3xl font-extrabold text-slate-900">
                {formatDuration(totals.study_seconds)}
              </p></div>
            </div>
            <div className="play-card flex -rotate-[.4deg] items-center gap-4 bg-pink-300 p-5" data-testid="progress-questions">
              <span className="play-icon h-16 w-16 bg-pink-600"><BrainCircuit className="h-9 w-9" /></span>
              <div><p className="text-sm font-extrabold text-pink-950/70">Retos resueltos</p>
              <p className="font-display text-3xl font-extrabold text-slate-900">{totals.questions_answered}</p></div>
            </div>
            <div className="play-card flex rotate-[.4deg] items-center gap-4 bg-lime-300 p-5" data-testid="progress-accuracy">
              <span className="play-icon h-16 w-16 bg-emerald-700"><Target className="h-9 w-9" /></span>
              <div><p className="text-sm font-extrabold text-lime-950/70">Puntería</p>
              <p className="font-display text-3xl font-extrabold text-slate-900">
                {Math.round((totals.accuracy || 0) * 100)}%
              </p></div>
            </div>
          </div>

          <section className="play-card bg-white p-5 sm:p-6" data-testid="progress-mastery">
            <h2 className="flex items-center gap-3 font-display text-2xl font-extrabold text-slate-900"><span className="play-icon h-12 w-12 bg-violet-600"><Medal className="h-7 w-7" /></span> Poder por tema</h2>
            <ul className="mt-4 space-y-4">
              {mastery.map((item) => (
                <li key={item.topic_id} data-testid="progress-mastery-row">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="truncate text-sm font-semibold text-slate-800">{item.topic}</p>
                    <span className="shrink-0 text-xs text-slate-400">{item.subject}</span>
                  </div>
                  <div className="mt-1.5">
                    <MasteryBar value={item.mastery} />
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <div className="grid gap-4 sm:grid-cols-2">
            <section className="play-card bg-emerald-100 p-5">
              <h2 className="flex items-center gap-2 font-display text-xl font-extrabold text-emerald-800"><Star className="h-6 w-6 fill-amber-300 text-amber-500" /> Tus superpoderes</h2>
              {strengths.length ? (
                <ul className="mt-3 space-y-1 text-sm text-slate-700">
                  {strengths.map((s) => (
                    <li key={s.topic_id}>{s.topic}</li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-xs text-slate-400">Practica un poco mas para verlo.</p>
              )}
            </section>
            <section className="play-card bg-orange-100 p-5">
              <h2 className="flex items-center gap-2 font-display text-xl font-extrabold text-orange-800"><RefreshCcw className="h-6 w-6" /> Próximos retos</h2>
              {weaknesses.length ? (
                <ul className="mt-3 space-y-1 text-sm text-slate-700">
                  {weaknesses.map((s) => (
                    <li key={s.topic_id}>{s.topic}</li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-xs text-slate-400">Nada critico por ahora.</p>
              )}
            </section>
          </div>

          <section className="play-card bg-white p-5 sm:p-6" data-testid="progress-sessions">
            <h2 className="font-display text-2xl font-extrabold text-slate-900">Tus últimas aventuras</h2>
            <ul className="mt-4 divide-y divide-slate-100 text-sm">
              {recent.map((session) => (
                <li key={session.id} className="flex items-center justify-between gap-3 py-2">
                  <span className="min-w-0">
                    <span className="block truncate font-semibold text-slate-800">{session.subject}</span>
                    <span className="text-xs text-slate-400">
                      {new Date(session.started_at).toLocaleDateString()} · {formatDuration(session.duration_seconds)}
                    </span>
                  </span>
                  <span className="shrink-0 font-display font-bold text-slate-700">
                    {session.answered ? `${Math.round((session.correct / session.answered) * 100)}%` : "—"}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}

function XpPill({ label, value, tone }) {
  return <div className={`rounded-2xl p-3 ${tone}`}><p className="text-xs font-black uppercase tracking-wide opacity-70">{label}</p><p className="font-display text-2xl font-black">+{value}</p></div>;
}

function Metric({ label, value, icon }) {
  return <div className="flex items-center gap-3 rounded-2xl bg-white/10 p-3"><span className="text-cyan-300">{icon}</span><div><p className="text-xs font-bold text-slate-400">{label}</p><p className="font-display text-lg font-extrabold text-white">{value}</p></div></div>;
}
